import { observable, action, computed, runInAction } from "mobx";
import escapeRegExp from "lodash/escapeRegExp";

import { getCurrentCulture } from "@utils";
import { EventFilterParams } from "@gp/hub";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { redirect } from "@state";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

const WORD_START = /^[\p{L}\p{N}_]/u;

type TConfig = {
	onTermChange?: (searchTerm: string) => void;
};

enum SearchType {
	StartsWith,
	AnyMiddleWord,
	AnyWord,
}

class BaseSearchBarViewStore {
	private config: TConfig = {};
	@observable searchTerm = "";

	@observable displayValidationPopup = false;

	@computed get trimSearchTerm() {
		return this.searchTerm ? this.searchTerm.trim() : "";
	}

	@computed get applySearch() {
		return this.searchTerm != null && this.trimSearchTerm !== "";
	}

	@computed get searchFilterArray(): EventFilterParams[] {
		return [
			this._getSearchObject(SearchType.StartsWith, "teamOne"),
			this._getSearchObject(SearchType.AnyMiddleWord, "teamOne"),

			this._getSearchObject(SearchType.StartsWith, "teamTwo"),
			this._getSearchObject(SearchType.AnyMiddleWord, "teamTwo"),

			this._getSearchObject(SearchType.StartsWith, "tournament"),
			this._getSearchObject(SearchType.AnyMiddleWord, "tournament"),

			this._getSearchObject(SearchType.StartsWith, "sportCategory"),
			this._getSearchObject(SearchType.AnyMiddleWord, "sportCategory"),

			this._getEventSearchObject(SearchType.StartsWith),
			this._getEventSearchObject(SearchType.AnyMiddleWord),
		];
	}

	constructor(config: TConfig = {}) {
		this.config = config;

		this._getSearchObject = this._getSearchObject.bind(this);
		this._getSearchString = this._getSearchString.bind(this);
	}

	@action.bound
	closeValidationPopup() {
		this.displayValidationPopup = false;
	}

	@action.bound
	public async onChange(term: string) {
		this.searchTerm = term;

		const isValid = (await lazy(loadFailPath, () => import("xregexp")))
			.default("^[\\p{L}0-9-. ]{4,30}$")
			.test(term);

		runInAction(() => {
			// validate
			if (!isValid) {
				this.displayValidationPopup = true;
				return;
			} else {
				this.displayValidationPopup = false;
			}

			redirect(
				`/${getCurrentCulture()}/search/full/${this.trimSearchTerm}`
			);
		});
	}

	@action.bound
	setValue(searchTerm: string) {
		this.searchTerm = searchTerm;

		if (this.config.onTermChange) {
			this.config.onTermChange(this.trimSearchTerm);
		}
	}

	@action.bound
	onReset() {
		this.searchTerm = "";

		if (this.config.onTermChange) {
			this.config.onTermChange("");
		}
	}

	private _getWordBoundary(value: string) {
		return WORD_START.test(value) ? "\\b" : "";
	}

	private _getAnySearchString(value: string) {
		return `.*${escapeRegExp(value)}.*`;
	}

	private _getSearchStringStartsWith(value: string) {
		return `${this._getWordBoundary(value)}${escapeRegExp(value)}\\w*`;
	}

	private _getSearchStringAnyMiddleWord(value: string) {
		return `\\b\\w+\\s+${this._getWordBoundary(value)}${escapeRegExp(
			value
		)}\\w*`;
	}

	private _getSearchString(type: SearchType, value: string) {
		switch (type) {
			case SearchType.StartsWith: {
				return this._getSearchStringStartsWith(value);
			}
			case SearchType.AnyMiddleWord: {
				return this._getSearchStringAnyMiddleWord(value);
			}
			default: {
				return this._getAnySearchString(value);
			}
		}
	}

	private _getSearchObject(
		type: SearchType,
		key: keyof EventFilterParams
	): EventFilterParams {
		return {
			[key]: {
				name: {
					iregex: this._getSearchString(type, this.trimSearchTerm),
				},
			},
		};
	}

	private _getEventSearchObject(type: SearchType): EventFilterParams {
		return {
			name: {
				iregex: this._getSearchString(type, this.trimSearchTerm),
			},
		};
	}
}

export default BaseSearchBarViewStore;
